/**
 * Daily Reward catalog and availability rules.
 *
 * Rewards are cosmetic only and apply to the on-screen player. Adding a
 * new reward = append a RewardDef to REWARD_CATALOG. No data migration.
 */

import type { ActiveReward, PlayerShape, DailyRewardState } from '@/storage/save-data';

export type { ActiveReward, PlayerShape };

const HOUR_MS = 60 * 60 * 1000;
export const CLAIM_COOLDOWN_MS = 23 * HOUR_MS;
export const REWARD_DURATION_MS = 24 * HOUR_MS;

/** Halve the wheel weight for any reward already in the player's recent history. */
const HISTORY_WEIGHT_FACTOR = 0.5;

export type RewardKind = ActiveReward['kind'];

export interface RewardDef {
  id: string;
  displayName: string;
  kind: RewardKind;
  /** Discriminated by `kind`. Used by `buildActiveReward`. */
  payload:
    | { kind: 'color'; color: string }
    | { kind: 'shape'; shape: PlayerShape }
    | { kind: 'googly-eyes' }
    | { kind: 'rainbow-trail' };
  wheelWeight: number;
}

// ─── Catalog ───────────────────────────────────────────────

export const REWARD_CATALOG: RewardDef[] = [
  // Colors
  { id: 'color-pink',   displayName: 'Hot Pink',  kind: 'color', wheelWeight: 1, payload: { kind: 'color', color: '#ff3399' } },
  { id: 'color-lime',   displayName: 'Lime',      kind: 'color', wheelWeight: 1, payload: { kind: 'color', color: '#88ff44' } },
  { id: 'color-orange', displayName: 'Tangerine', kind: 'color', wheelWeight: 1, payload: { kind: 'color', color: '#ff8844' } },
  { id: 'color-violet', displayName: 'Violet',    kind: 'color', wheelWeight: 1, payload: { kind: 'color', color: '#aa55ff' } },
  { id: 'color-white',  displayName: 'Snow',      kind: 'color', wheelWeight: 1, payload: { kind: 'color', color: '#ffffff' } },
  { id: 'color-gold',   displayName: 'Gold',      kind: 'color', wheelWeight: 1, payload: { kind: 'color', color: '#ffcc33' } },
  // Shapes
  { id: 'shape-right',  displayName: 'Right Triangle', kind: 'shape', wheelWeight: 1, payload: { kind: 'shape', shape: 'right-triangle' } },
  { id: 'shape-square', displayName: 'Square',         kind: 'shape', wheelWeight: 1, payload: { kind: 'shape', shape: 'square' } },
  { id: 'shape-penta',  displayName: 'Pentagon',       kind: 'shape', wheelWeight: 1, payload: { kind: 'shape', shape: 'pentagon' } },
  { id: 'shape-circle', displayName: 'Circle',         kind: 'shape', wheelWeight: 1, payload: { kind: 'shape', shape: 'circle' } },
  // Cosmetic / effect
  { id: 'cosmetic-eyes',  displayName: 'Googly Eyes',   kind: 'googly-eyes',   wheelWeight: 1, payload: { kind: 'googly-eyes' } },
  { id: 'effect-rainbow', displayName: 'Rainbow Trail', kind: 'rainbow-trail', wheelWeight: 1, payload: { kind: 'rainbow-trail' } },
];

export function rewardById(id: string): RewardDef | undefined {
  return REWARD_CATALOG.find((r) => r.id === id);
}

export function buildActiveReward(def: RewardDef): ActiveReward {
  switch (def.payload.kind) {
    case 'color':         return { kind: 'color',         rewardId: def.id, color: def.payload.color };
    case 'shape':         return { kind: 'shape',         rewardId: def.id, shape: def.payload.shape };
    case 'googly-eyes':   return { kind: 'googly-eyes',   rewardId: def.id };
    case 'rainbow-trail': return { kind: 'rainbow-trail', rewardId: def.id };
  }
}

// ─── Availability ──────────────────────────────────────────

export function canClaim(claimedAt: number | undefined, nowMs: number): boolean {
  if (claimedAt === undefined) return true;
  return nowMs - claimedAt >= CLAIM_COOLDOWN_MS;
}

export function isActive(claimedAt: number | undefined, nowMs: number): boolean {
  if (claimedAt === undefined) return false;
  return nowMs - claimedAt < REWARD_DURATION_MS;
}

export function hoursUntilClaim(claimedAt: number | undefined, nowMs: number): number {
  if (claimedAt === undefined) return 0;
  const remaining = CLAIM_COOLDOWN_MS - (nowMs - claimedAt);
  if (remaining <= 0) return 0;
  return Math.ceil(remaining / HOUR_MS);
}

export function getActiveReward(state: DailyRewardState | undefined, nowMs: number): ActiveReward | null {
  if (!state) return null;
  if (!isActive(state.claimedAt, nowMs)) return null;
  return state.reward;
}

// ─── Random selection ──────────────────────────────────────

/** Reduce weight by half for any reward whose id appears in `history`. */
function adjustedWeight(def: RewardDef, history: string[]): number {
  return history.includes(def.id) ? def.wheelWeight * HISTORY_WEIGHT_FACTOR : def.wheelWeight;
}

export function pickWeightedReward(
  pool: RewardDef[],
  history: string[],
  rng: () => number = Math.random,
): RewardDef {
  if (pool.length === 0) throw new Error('pickWeightedReward: empty pool');
  const weights = pool.map((d) => adjustedWeight(d, history));
  const total = weights.reduce((a, b) => a + b, 0);
  const target = rng() * total;
  let acc = 0;
  for (let i = 0; i < pool.length; i++) {
    acc += weights[i];
    if (target < acc) return pool[i];
  }
  return pool[pool.length - 1];
}

/**
 * Pick 8 wedges for the spin wheel. The winner is always included; the other
 * 7 are sampled (without replacement) from the rest of the catalog with
 * history bias applied. The result is shuffled so the winner's wedge angle
 * is randomized.
 */
export function pickWheelRewards(
  winner: RewardDef,
  catalog: RewardDef[],
  history: string[],
  rng: () => number = Math.random,
): RewardDef[] {
  const others = catalog.filter((d) => d.id !== winner.id);
  const slots = Math.min(7, others.length);
  const picked: RewardDef[] = [];
  let pool = others.slice();
  for (let i = 0; i < slots; i++) {
    const choice = pickWeightedReward(pool, history, rng);
    picked.push(choice);
    pool = pool.filter((d) => d.id !== choice.id);
  }
  const all = [winner, ...picked];
  // Fisher–Yates shuffle for wedge order
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]];
  }
  return all;
}
